import { FileText } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import type {
  SchemaEvidencePointer,
  SchemaMinimum,
  SchemaProposal,
  SchemaReferenceFile,
} from '@/services/api/schemaSessions'

export function SourceFileLink({ file }: { file: SchemaReferenceFile }) {
  const { pathname } = useLocation()
  return file.missing ? (
    <span className="text-muted-foreground">{file.filename}</span>
  ) : (
    <Link
      to={`${pathname}?doc=${encodeURIComponent(file.document_id)}`}
      className="inline-flex items-start gap-1 text-primary hover:underline break-words"
    >
      <FileText className="mt-0.5 size-3 shrink-0" />
      {file.filename}
    </Link>
  )
}

export function EvidenceLinks({
  evidence,
  sources,
}: {
  evidence: SchemaEvidencePointer[]
  sources: SchemaReferenceFile[]
}) {
  const { t } = useTranslation()
  return (
    <ul className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs">
      {evidence.map((pointer, index) => {
        const file = sources.find(
          source => source.document_id === pointer.ref || source.filename === pointer.ref
        )
        return (
          <li key={`${pointer.kind}:${pointer.ref}:${index}`}>
            {pointer.kind === 'document' && file ? (
              <SourceFileLink file={file} />
            ) : (
              <span className="text-muted-foreground">
                {t(`schemaAgent.sources.pointer.${pointer.kind}`)}:{' '}
                {pointer.kind === 'form'
                  ? t(`schemaAgent.sources.form.${pointer.ref}`, { defaultValue: pointer.ref })
                  : pointer.ref}
              </span>
            )}
          </li>
        )
      })}
    </ul>
  )
}

export function SchemaSources({
  initial,
  proposal,
  minimum,
}: {
  initial: SchemaReferenceFile[]
  proposal?: SchemaProposal | null
  /** The library schema a session without files keeps (PRD-0073). */
  minimum?: SchemaMinimum | null
}) {
  const { t } = useTranslation()
  if (initial.length === 0) {
    return minimum ? (
      <p className="rounded-lg border bg-muted/20 px-4 py-3 text-sm text-muted-foreground">
        {t('schemaAgent.sources.none', { name: minimum.name, version: minimum.version })}
      </p>
    ) : null
  }
  return (
    <details className="rounded-lg border bg-muted/20 px-4 py-3 text-sm" open>
      <summary className="cursor-pointer font-medium">
        {t('schemaAgent.sources.title', { count: initial.length })}
      </summary>
      <p className="mt-2 text-xs text-muted-foreground">{t('schemaAgent.sources.initialHint')}</p>
      <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-2 text-xs">
        {initial.map(file => (
          <li key={file.document_id}>
            <SourceFileLink file={file} />
          </li>
        ))}
      </ul>
      {proposal && <DocumentRelevance proposal={proposal} />}
      {proposal && !proposal.documentReviews?.length && (
        <p className="mt-2 text-xs text-muted-foreground">{t('schemaAgent.sources.legacy')}</p>
      )}
    </details>
  )
}

function DocumentRelevance({ proposal }: { proposal: SchemaProposal }) {
  const { t } = useTranslation()
  if (!proposal.documentReviews?.length) return null
  const files = proposal.sources ?? []
  return (
    <details className="mt-3 border-t pt-3">
      <summary className="cursor-pointer text-xs font-medium">
        {t('schemaAgent.sources.relevanceTitle')}
      </summary>
      <p className="mt-2 text-xs text-muted-foreground">{t('schemaAgent.sources.relevanceHint')}</p>
      <ul className="mt-3 grid gap-3 md:grid-cols-2">
        {proposal.documentReviews?.map(review => {
          const file = files.find(source => source.document_id === review.document_id)
          return (
            <li key={review.document_id} className="rounded-md border bg-card p-3 text-xs">
              <div className="flex flex-wrap items-start justify-between gap-2">
                {file ? <SourceFileLink file={file} /> : review.document_id}
                <Badge variant="secondary">
                  {t(`schemaAgent.sources.relevance.${review.relevance}`)}
                </Badge>
              </div>
              <p className="mt-2 text-muted-foreground">{review.reason}</p>
            </li>
          )
        })}
      </ul>
    </details>
  )
}
